import { AbstractCollection } from '../../abstract/Collection';
import { AbstractDocument } from '../../abstract/Document';
import type { ISupplier_Type, ISupplierID } from '../Suppliers.types';
import { type Wedding, Weddings } from '../Weddings';
import { type IInteraction, IInteractionAction } from './Interactions.types';
import { orderBy, type QueryConstraint, where, limit } from 'firebase/firestore';

export class Interaction extends AbstractDocument<IInteraction> {
  readonly collections = {};

  /**
   * Returns the time of the *FCR* (first couple response) in an `enquiry` interaction.
   *
   * @param snapshot The `Interaction` snapshot.
   */
  static fcr(snapshot?: IInteraction): Date | false {
    if (snapshot?.action !== IInteractionAction.ENQUIRY || snapshot?.messages == null) {
      return false;
    }

    /**
     * Messages should already be sorted by time, in chronological order.
     * We sort again to handle rare cases of delayed delivery or timestamp drift.
     * This should result in minimal reshuffling, if any.
     */
    const messages = Object.entries(snapshot.messages)
      .map(([date, from]) => ({ time: new Date(date), from }))
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    if (messages.length <= 3) {
      return false;
    }

    let previous = messages.shift()!.from;

    for (const message of messages) {
      const current = message.from;

      if (previous === 'supplier' && current === 'couple') {
        return message.time;
      }

      previous = current;
    }

    return false;
  }
}

export class Interactions extends AbstractCollection<Interaction, IInteraction> {
  static path = 'interactions';

  constructor(document: Wedding) {
    super(document.collection(Interactions.path), Interaction);
  }

  get wedding() {
    return Weddings._.getById(this.reference.parent!.id);
  }

  /**
   * Returns all `Interaction` documents that satisfy the constraints, ordered chronologically.
   *
   * @param id - The Supplier ID, optional.
   * @param type - The Supplier Type, optional.
   * @param action - The Interaction Action, optional.
   */
  getBySupplier(id?: ISupplierID, type?: ISupplier_Type, action?: IInteractionAction) {
    const constraints: QueryConstraint[] = [where('reverted', '==', false), orderBy('timestamp', 'desc'), limit(100)];

    if (id != null) {
      constraints.push(where('supplier.id', '==', id));
    }

    if (type != null) {
      constraints.push(where('supplier.type', '==', type));
    }

    if (action != null) {
      constraints.push(where('action', '==', action));
    }

    return this.query(constraints).get(true);
  }

  getInteractionsCount(type?: ISupplier_Type) {
    const constraints: QueryConstraint[] = [where('reverted', '==', false), orderBy('timestamp', 'desc')];

    if (type != null) {
      constraints.push(where('supplier.type', '==', type));
    }

    return this.query(constraints).count();
  }

  /**
   * Returns the first couple response (FCR) date for the given supplier.
   *
   * @param supplier - The Supplier ID.
   */
  async getSupplierFCR(supplier: ISupplierID) {
    const enquiries = Object.values(await this.getBySupplier(supplier, undefined, IInteractionAction.ENQUIRY));

    if (enquiries.length === 0) {
      return false;
    }

    for (const enquiry of enquiries) {
      const fcr = Interaction.fcr(enquiry);

      if (fcr !== false) {
        return fcr;
      }
    }

    return false;
  }
}
